import { useState } from 'react';
import type { InboundMailbox, InboundVerification } from '@/api/hooks/useInboundMailbox';
import { useDismissInboundVerification } from '@/api/hooks/useInboundMailbox';

export function HostLink({ href, children }: { href: string; children: string }) {
  return (
    <a
      href={href}
      target="_blank"
      rel="noreferrer"
      className="ms-tap-44 inline-flex min-h-11 items-center text-sm text-link underline"
    >
      {children}
    </a>
  );
}

export function GmailDeepLinks({
  label,
  hrefFor,
  emails,
}: {
  label: string;
  hrefFor: (account: { authuser?: string; index?: number }) => string;
  emails: string[];
}) {
  if (emails.length === 0) {
    return <HostLink href={hrefFor({ index: 0 })}>{label}</HostLink>;
  }
  return (
    <div className="flex flex-col items-start gap-3">
      {emails.map((email) => (
        <HostLink key={email.toLowerCase()} href={hrefFor({ authuser: email })}>
          {`${label} (${email})`}
        </HostLink>
      ))}
    </div>
  );
}

function confirmationLabel(row: InboundVerification): string {
  const hostLabel = row.host === 'gmail'
    ? 'Open Gmail confirmation'
    : row.host === 'outlook'
      ? 'Open Outlook confirmation'
      : 'Open forwarding confirmation';
  return row.from_email ? `${hostLabel} (${row.from_email})` : hostLabel;
}

export function confirmationRows(mailbox: Pick<
  InboundMailbox,
  'verifications' | 'verification_code' | 'verification_url' | 'verification_host'
>): InboundVerification[] {
  if ((mailbox.verifications ?? []).length > 0) return mailbox.verifications;
  if (!mailbox.verification_code && !mailbox.verification_url) return [];
  return [{
    message_id: '00000000-0000-0000-0000-000000000000',
    host: mailbox.verification_host ?? 'gmail',
    code: mailbox.verification_code,
    url: mailbox.verification_url,
    from_email: null,
  }];
}

export function ConfirmationRows({
  mailbox,
}: {
  mailbox: Pick<InboundMailbox, 'verifications' | 'verification_code' | 'verification_url' | 'verification_host'>;
}) {
  const dismiss = useDismissInboundVerification();
  const rows = confirmationRows(mailbox);
  if (rows.length === 0) {
    return <p className="text-sm text-muted-foreground">No forwarding confirmation code is available.</p>;
  }
  return (
    <div className="space-y-3">
      {rows.map((row) => (
        <div key={row.message_id} className="space-y-2">
          <div className="flex items-start gap-2">
            {row.url && (
              <HostLink href={row.url}>{confirmationLabel(row)}</HostLink>
            )}
            <button
              type="button"
              onClick={() => dismiss.mutate(row.message_id)}
              disabled={dismiss.isPending}
              className="ms-tap-44 inline-flex min-h-11 min-w-11 shrink-0 items-center justify-center text-lg leading-none text-muted-foreground hover:text-foreground"
              aria-label={row.from_email ? `Dismiss confirmation for ${row.from_email}` : 'Dismiss confirmation'}
            >
              ×
            </button>
          </div>
          {row.code && (
            <CopyField
              id={`receipt-forwarding-confirmation-code-${row.message_id}`}
              label="Forwarding confirmation code"
              value={row.code}
            />
          )}
        </div>
      ))}
    </div>
  );
}

export function CopyField({
  id,
  label,
  value,
  testId,
  multiline = false,
}: {
  id: string;
  label: string;
  value: string;
  testId?: string;
  multiline?: boolean;
}) {
  const [copied, setCopied] = useState(false);

  const copy = async () => {
    await navigator.clipboard.writeText(value);
    setCopied(true);
  };

  return (
    <div className="space-y-1.5">
      <label htmlFor={id} className="text-sm font-medium">{label}</label>
      <div className="flex min-h-11 rounded-md border border-input bg-background focus-within:ring-2 focus-within:ring-ring focus-within:ring-offset-2">
        {multiline ? (
          <textarea
            id={id}
            readOnly
            value={value}
            data-testid={testId}
            rows={12}
            onFocus={(event) => event.currentTarget.select()}
            className="min-h-32 min-w-0 flex-1 resize-y border-0 bg-transparent px-3 py-2 font-mono text-xs outline-none sm:text-sm"
          />
        ) : (
          <input
            id={id}
            readOnly
            value={value}
            data-testid={testId}
            onFocus={(event) => event.currentTarget.select()}
            className="min-h-11 min-w-0 flex-1 border-0 bg-transparent px-3 font-mono text-xs outline-none sm:text-sm"
          />
        )}
        <button
          type="button"
          onClick={() => void copy()}
          className="min-h-11 shrink-0 self-stretch border-l border-input px-4 text-sm hover:bg-muted"
          aria-label={copied ? 'Copied' : `Copy ${label.toLowerCase()}`}
          aria-live="polite"
        >
          {copied ? 'Copied' : 'Copy'}
        </button>
      </div>
    </div>
  );
}
